import { TypeIdSchema } from '@unipile/db';
import { Static, Type } from '@sinclair/typebox';
import { OauthProviderSchema } from '../common/provider.schema';
import { CustomProxySchema } from '../common/proxys.schema';

/**
 * The verification method to switch to when requesting a checkpoint on a CHALLENGE_SELECTION
 * checkpoint. Matches the `id` of one of the entries in the checkpoint's `challenges` list.
 */
export const RequestCheckpointChallengeSchema = Type.String({
  description:
    'The alternative verification method to switch to. Must be one of the `id`s listed in the CHALLENGE_SELECTION checkpoint.',
});

export const OauthScopeOptionSchema = Type.Optional(
  Type.Array(Type.String({ minLength: 1 }), {
    minItems: 1,
    description:
      'The final list of authorizations to request from the Provider. Use this field to expand or narrow the scope of API access. For example, if your application only uses the Calendar API, you may request authorization solely for the calendar, excluding email access for providers like Google. Make sure to give only authorizations accepted by your registered provider application. For a new account, omitting this field requests the authorizations defined in the Provider OAuth settings. For a reconnect, omitting it preserves already granted authorizations and adds the authorizations currently defined in the Provider OAuth settings.',
  }),
);

export const StartAuthIntentBodyBaseSchema = Type.Object({
  account_scope_id: Type.Optional(
    TypeIdSchema('accountScope', {
      description: 'The Scope the account will be assigned to once authentication is complete.',
    }),
  ),
  state: Type.Optional(
    Type.String({
      description: 'State data sent in the `account.add` / `account.reconnect` webhook payload after the authentication process.',
    }),
  ),
  account_id: Type.Optional(
    TypeIdSchema('account', {
      description:
        'The ID of the Account to re-authenticate or reconfigure. The account may be running, partially connected, or disconnected, and `provider` must match its provider. Reconnecting always refreshes the entire account and restarts all configured products. If not specified, a new account is linked.',
    }),
  ),
});

/**
 * Solve Checkpoint
 */
export const ProviderAuthenticationCheckpointInputSchema = Type.Object({
  code: Type.String({
    description:
      'The code to solve the checkpoint. Check the AuthenticationCheckpoint response you have already obtained to see what type of code is expected.',
  }),
  credential: Type.Optional(
    Type.String({
      description:
        'The secret that goes with the chosen option, when the checkpoint asks for one. On ENCRYPTED_HISTORY, `code` names the option and this carries the PIN or the recovery code; it is left out when the option needs no secret.',
    }),
  ),
});

export const SolveCheckpointBodySchema = Type.Composite([
  ProviderAuthenticationCheckpointInputSchema,
  Type.Object({
    intent_id: TypeIdSchema('account', { description: 'The ID of the intent for which a checkpoint must be solved.' }),
  }),
]);

export const RequestCheckpointBodySchema = Type.Object({
  intent_id: TypeIdSchema('account', { description: 'The ID of the intent for which a checkpoint must be requested.' }),
  challenge: Type.Optional(RequestCheckpointChallengeSchema),
});

export const ResendCheckpointBodySchema = Type.Object({
  intent_id: TypeIdSchema('account', { description: 'The ID of the intent for which a checkpoint must be resent.' }),
});

/**
 * Oauth
 */
export const OauthCallbackQuerySchema = Type.Union([
  Type.Object({
    code: Type.String({ description: 'The code received from the oauth provider.' }),
    state: Type.String({ description: 'The state received from the oauth provider.' }),
  }),
  Type.Object({
    error: Type.String({ description: 'The error received from the oauth provider.' }),
    state: Type.String({ description: 'The state received from the oauth provider.' }),
  }),
]);

export const OauthAuthenticationConfigSchema = Type.Object(
  {
    custom_proxy: Type.Optional(CustomProxySchema),
    oauth_scope: OauthScopeOptionSchema,
  },
  {
    additionalProperties: true, // Allow additional properties because Hosted Auth forward its own config
    description: 'Oauth authentication configuration.',
  },
);

export const OauthAuthenticationInputSchema = Type.Object(
  {
    provider: OauthProviderSchema,
    /**
     * @note This is the redirect_uri for the redirection made by Unipile on the /auth/oauth/callback route, NOT the redirect_uri for the oAuth flow (which is defined always unipile.com/v2/auth/oauth/callback).
     */
    oauth_callback_redirect_uri: Type.String({
      description: "The URL to redirect to after the user has authenticated using the Provider's oAuth screen.",
    }),
    login_hint: Type.Optional(
      Type.String({
        description:
          'Uses the hint to simplify the login flow either by prefilling the email / username / phone number field in the authentication form.',
      }),
    ),
    config: Type.Optional(OauthAuthenticationConfigSchema),
  },
  {
    title: 'OAuth (Google, Outlook)',
    description:
      'Start browser-based authorization with Google or Outlook. No credentials are submitted in this request; the user authenticates on the provider authorization page.',
  },
);

export type ProviderAuthenticationCheckpointInput = Static<typeof ProviderAuthenticationCheckpointInputSchema>;
export type SolveCheckpointBody = Static<typeof SolveCheckpointBodySchema>;
export type RequestCheckpointBody = Static<typeof RequestCheckpointBodySchema>;
export type RequestCheckpointChallenge = Static<typeof RequestCheckpointChallengeSchema>;
export type ResendCheckpointBody = Static<typeof ResendCheckpointBodySchema>;
export type OauthAuthenticationInput = Static<typeof OauthAuthenticationInputSchema>;
